/**
 * Lanes: the routes pieces take between phases, and when they take them.
 *
 * A swim, pass or shot drawn from a piece names that piece, and is a
 * stroke of its route into the phase the stroke is drawn on, from the one
 * before. The coach drafting a phase sees the piece land where the stroke
 * ends, and playback moves it there along the stroke, curves included, rather
 * than cutting the straight line between the two positions.
 *
 * A phase reads like a story told in the order it was drawn — "5 swims to the
 * ball, passes to 7, 7 passes to 3" — so its strokes are scheduled in drawn
 * order. Each one starts as soon as every piece it involves is free: the piece
 * it moves, whatever that piece carries, and the player a pass is played from.
 * Strokes that share no piece start together. Every piece moves at one pace,
 * so the schedule decides only when things start, never how fast they move.
 *
 * A stroke's points are what the coach drew; the route is those points
 * stretched to run from where the piece actually is when the stroke starts to
 * where it lands. Either end can move after the stroke was drawn — a drag, a
 * deleted phase, an earlier stroke — and the route still joins the move it
 * describes, because it is derived here rather than stored.
 */

import { add, distance, lerpVec, polylineLength, sub, type Vec2 } from './geometry.ts';
import type { PlayDocument, Shape } from './document.ts';
import type { Id } from './ids.ts';

/** How distance is turned into time. */
export interface Measure {
  /** Field width ÷ height, so distance is measured in the real shape of the field. */
  readonly aspectRatio: number;
  /** Milliseconds for one field length. */
  readonly paceMs: number;
}

/** A piece carried along by a stroke, e.g. the ball a swimmer has. */
export interface CarriedMove {
  readonly id: Id;
  /** Where it sits relative to the mover as the stroke starts, and as it ends. */
  readonly offsetFrom: Vec2;
  readonly offsetTo: Vec2;
}

/** One stroke of one transition, placed in space and time. */
export interface ScheduledStroke {
  readonly shapeId: Id;
  readonly moverId: Id;
  /** The drawn stroke, stretched to where the mover is when it starts and where it ends. */
  readonly points: readonly Vec2[];
  readonly carried: readonly CarriedMove[];
  /** Milliseconds into the transition. */
  readonly start: number;
  readonly duration: number;
}

/** Everything that moves by a stroke in one transition. */
export interface Choreography {
  readonly strokes: readonly ScheduledStroke[];
  /** When the last stroke ends, in milliseconds into the transition. */
  readonly finish: number;
  /** Pieces moved by at least one stroke, as mover or carried. The rest take the straight line. */
  readonly choreographed: ReadonlySet<Id>;
}

const EMPTY: Choreography = { strokes: [], finish: 0, choreographed: new Set() };

/**
 * The strokes of the transition into `keyframeId`, in drawn order, each placed
 * and timed. Empty for the first phase (there is nothing to move from: a
 * stroke there only sets a piece up), and without strokes that name a piece.
 */
export function choreographyInto(doc: PlayDocument, keyframeId: Id, measure: Measure): Choreography {
  const index = doc.keyframes.findIndex((k) => k.id === keyframeId);
  if (index < 1) return EMPTY;
  const from = doc.keyframes[index - 1]!.positions;
  const to = doc.keyframes[index]!.positions;
  const known = (id: Id) => from[id] !== undefined && to[id] !== undefined;

  const shapes = doc.shapes.filter(
    (shape) =>
      shape.keyframeId === keyframeId && shape.moverId !== undefined && shape.points.length >= 2 && known(shape.moverId),
  );
  const carriedOf = (shape: Shape) => (shape.carriedIds ?? []).filter((id) => id !== shape.moverId && known(id));

  // A piece's last stroke in the transition ends where the phase has it; the
  // ones before end where they were drawn to.
  const last = new Map<Id, number>();
  shapes.forEach((shape, i) => {
    for (const id of [shape.moverId!, ...carriedOf(shape)]) last.set(id, i);
  });

  const current: Record<Id, Vec2> = { ...from };
  const free = new Map<Id, number>();
  const strokes: ScheduledStroke[] = [];

  shapes.forEach((shape, i) => {
    const moverId = shape.moverId!;
    const carriedIds = carriedOf(shape);
    const start = current[moverId]!;
    const end = last.get(moverId) === i ? to[moverId]! : shape.points[shape.points.length - 1]!;
    const points = stretch(shape.points, start, end);

    const carried = carriedIds.map((id) => {
      const offsetFrom = sub(current[id]!, start);
      const offsetTo = last.get(id) === i ? sub(to[id]!, end) : offsetFrom;
      current[id] = add(end, offsetTo);
      return { id, offsetFrom, offsetTo };
    });
    current[moverId] = end;

    // The player a pass is played from is part of it until the ball leaves.
    const passer = shape.fromId !== undefined && shape.fromId !== moverId ? [shape.fromId] : [];
    const moving = [moverId, ...carriedIds];
    const begin = Math.max(0, ...[...moving, ...passer].map((id) => free.get(id) ?? 0));
    const duration = routeLength(points, measure.aspectRatio) * measure.paceMs;
    for (const id of moving) free.set(id, begin + duration);
    for (const id of passer) free.set(id, begin);

    strokes.push({ shapeId: shape.id, moverId, points, carried, start: begin, duration });
  });

  return {
    strokes,
    finish: Math.max(0, ...strokes.map((s) => s.start + s.duration)),
    choreographed: new Set(last.keys()),
  };
}

/**
 * Where a choreographed piece is `elapsed` ms into the transition: waiting at
 * its start before its first stroke, moving through each stroke it is in, and
 * waiting where each one leaves it between and after them.
 */
export function choreographedAt(
  choreography: Choreography,
  id: Id,
  from: Vec2,
  elapsed: number,
  measure: Measure,
): Vec2 {
  let at = from;
  for (const stroke of choreography.strokes) {
    const role = stroke.moverId === id ? undefined : stroke.carried.find((c) => c.id === id);
    if (stroke.moverId !== id && !role) continue;
    const progress = alongRoute(stroke.points, elapsed - stroke.start, measure);
    if (!role) {
      at = progress.at;
    } else {
      at = add(progress.at, lerpVec(role.offsetFrom, role.offsetTo, progress.fraction));
    }
    if (elapsed < stroke.start + stroke.duration) return at;
  }
  return at;
}

/** What the board draws for a stroke: its route when it moves a piece, else the stroke as drawn. */
export function lanePoints(doc: PlayDocument, shape: Shape): readonly Vec2[] {
  // Geometry does not depend on time, so any measure will do.
  const stroke = choreographyInto(doc, shape.keyframeId, { aspectRatio: 1, paceMs: 1 }).strokes.find(
    (s) => s.shapeId === shape.id,
  );
  return stroke?.points ?? shape.points;
}

/* ------------------------------------------------------------------ */
/* Measuring routes                                                    */
/* ------------------------------------------------------------------ */

/** One leg's length with the long axis scaled, so it is measured in the field's real shape. */
function legLength(a: Vec2, b: Vec2, aspectRatio: number): number {
  return Math.hypot((b.x - a.x) * aspectRatio, b.y - a.y);
}

/** A route's length, measured as `legLength` measures. */
export function routeLength(points: readonly Vec2[], aspectRatio: number): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) total += legLength(points[i - 1]!, points[i]!, aspectRatio);
  return total;
}

/**
 * Where a piece is `elapsed` ms into a route it takes at the measure's pace,
 * and how far along the route that is (0–1). Before the route starts it is at
 * the beginning; once it is done it waits at the end.
 */
export function alongRoute(points: readonly Vec2[], elapsed: number, measure: Measure): { at: Vec2; fraction: number } {
  const length = routeLength(points, measure.aspectRatio);
  const last = points[points.length - 1]!;
  if (length === 0) return { at: last, fraction: 1 };
  const target = Math.min(length, Math.max(0, elapsed / measure.paceMs));
  let travelled = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const leg = legLength(a, b, measure.aspectRatio);
    if (travelled + leg >= target) {
      return { at: lerpVec(a, b, leg === 0 ? 0 : (target - travelled) / leg), fraction: target / length };
    }
    travelled += leg;
  }
  return { at: last, fraction: 1 };
}

/**
 * Bends `points` so they run from `start` to `end`: each point shifts by a
 * blend of the two ends' corrections, weighted by how far along the route it
 * sits. The shape the coach drew survives; only its ends are pinned.
 */
function stretch(points: readonly Vec2[], start: Vec2, end: Vec2): Vec2[] {
  const head = sub(start, points[0]!);
  const tail = sub(end, points[points.length - 1]!);
  const total = polylineLength(points);
  const last = points.length - 1;
  let travelled = 0;

  return points.map((point, i) => {
    // Pinned exactly rather than shifted, so the ends never drift by a rounding error.
    if (i === 0) return start;
    if (i === last) return end;
    travelled += distance(points[i - 1]!, point);
    const along = total === 0 ? i / last : travelled / total;
    return add(point, lerpVec(head, tail, along));
  });
}
